import { isTouchEnabled } from '../../../../../../Theme';
import { MediaFormatEnum } from '../../../../../general-settings/enums/mediaFormat.enum';
import { COMBINATION_PRICE_SECTION_WIDTH } from '../../../../../products/products-details-v2/components/CombinationLine/consts/combinationLine.consts';

type CatalogListTouchAxis = Record<'touch' | 'mouse', number>;

export const CATALOG_LIST_ROW_VERTICAL_PADDING = 12;

export const CATALOG_LIST_ROW_TAB_INDEX = -1;

export const CATALOG_LIST_CHIPS_TO_GRID_GAP = 16;

export const CATALOG_LIST_CHIPS_GAP = 4;

export const CATALOG_LIST_TAG_CHIP_HEIGHT = 16;

export const CATALOG_LIST_STACKED_INFO_TO_CHIPS_GAP = 12;

const CATALOG_LIST_INFO_COLUMN_GAP = 8;
const CATALOG_LIST_HEADING_HEIGHT = 37;
const CATALOG_LIST_COLOR_DOTS_HEIGHT = 18;

export const CATALOG_LIST_INFO_COLUMN_HEIGHT =
  CATALOG_LIST_TAG_CHIP_HEIGHT +
  CATALOG_LIST_INFO_COLUMN_GAP +
  CATALOG_LIST_HEADING_HEIGHT +
  CATALOG_LIST_INFO_COLUMN_GAP +
  CATALOG_LIST_COLOR_DOTS_HEIGHT;

export const CATALOG_LIST_CHIPS_STRIP_HEIGHT: CatalogListTouchAxis = { touch: 38, mouse: 24 };

const CATALOG_LIST_GRID_BUTTON_HEIGHT = 38;
const CATALOG_LIST_GRID_BUTTON_COLUMN_GAP = 4;
const CATALOG_LIST_ADD_BUTTON_HEIGHT = 38;
const CATALOG_LIST_GRID_SCROLL_PADDING_BOTTOM = 3;

const CATALOG_LIST_STACKED_GRID_BLOCK_HEIGHT =
  CATALOG_LIST_GRID_BUTTON_HEIGHT +
  CATALOG_LIST_GRID_BUTTON_COLUMN_GAP +
  CATALOG_LIST_ADD_BUTTON_HEIGHT +
  CATALOG_LIST_GRID_SCROLL_PADDING_BOTTOM;

export const CATALOG_LIST_GRID_BLOCK_HEIGHT = {
  threeColumns: { touch: 100, mouse: 56 },
  stacked: {
    touch: 100,
    mouse: CATALOG_LIST_STACKED_GRID_BLOCK_HEIGHT,
  },
};

const CATALOG_LIST_CHIPS_FADE_WIDTH = 16;

export const CATALOG_LIST_CHIPS_SLOT_SX = {
  minHeight: CATALOG_LIST_CHIPS_STRIP_HEIGHT[isTouchEnabled ? 'touch' : 'mouse'],
  minWidth: 0,
  flex: '1 1 auto',
} as const;

export const CATALOG_LIST_STACKED_CHIPS_SLOT_SX = {
  ...CATALOG_LIST_CHIPS_SLOT_SX,
  overflowX: 'auto',
  overflowY: 'hidden',
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': { display: 'none' },
  scrollPaddingInlineEnd: `${CATALOG_LIST_CHIPS_FADE_WIDTH}px`,
  maskImage: `linear-gradient(to right, #000 calc(100% - ${CATALOG_LIST_CHIPS_FADE_WIDTH}px), transparent)`,
  WebkitMaskImage: `linear-gradient(to right, #000 calc(100% - ${CATALOG_LIST_CHIPS_FADE_WIDTH}px), transparent)`,
} as const;

export const CATALOG_LIST_IMAGE_BLOCK_HEIGHT = {
  threeColumns: { touch: 160, mouse: 85 },
  stacked: 83,
};

const CATALOG_LIST_IMAGE_BLOCK_WIDTH = {
  threeColumns: { touch: 100, mouse: 54 },
  stacked: 52,
};

interface CatalogListRowAxesParams {
  isStacked: boolean;
  isTouchEnabled: boolean;
}

const getCatalogListCenterBlockHeight = ({
  isStacked,
  isTouchEnabled,
}: CatalogListRowAxesParams): number => {
  const touchKey = isTouchEnabled ? 'touch' : 'mouse';

  return (
    CATALOG_LIST_CHIPS_STRIP_HEIGHT[touchKey] +
    CATALOG_LIST_CHIPS_TO_GRID_GAP +
    CATALOG_LIST_GRID_BLOCK_HEIGHT[isStacked ? 'stacked' : 'threeColumns'][touchKey]
  );
};

export const getCatalogListImageHeight = ({
  isStacked,
  isTouchEnabled,
}: CatalogListRowAxesParams): number => {
  if (isStacked) {
    return CATALOG_LIST_IMAGE_BLOCK_HEIGHT.stacked;
  }

  return Math.max(
    CATALOG_LIST_IMAGE_BLOCK_HEIGHT.threeColumns[isTouchEnabled ? 'touch' : 'mouse'],
    getCatalogListCenterBlockHeight({ isStacked, isTouchEnabled }),
  );
};

interface GetCatalogListImageSizeParams extends CatalogListRowAxesParams {
  mediaFormat: MediaFormatEnum;
}

export const getCatalogListTopBlockHeight = ({
  isStacked,
  isTouchEnabled,
}: CatalogListRowAxesParams): number =>
  Math.max(
    getCatalogListImageHeight({ isStacked, isTouchEnabled }),
    CATALOG_LIST_INFO_COLUMN_HEIGHT,
  );

export const getCatalogListImageSize = ({
  isStacked,
  isTouchEnabled,
  mediaFormat,
}: GetCatalogListImageSizeParams) => {
  const height = getCatalogListImageHeight({ isStacked, isTouchEnabled });

  if (mediaFormat === MediaFormatEnum.SQUARE) {
    return { width: height, height };
  }

  if (isStacked) {
    return { width: CATALOG_LIST_IMAGE_BLOCK_WIDTH.stacked, height };
  }

  return {
    width: CATALOG_LIST_IMAGE_BLOCK_WIDTH.threeColumns[isTouchEnabled ? 'touch' : 'mouse'],
    height,
  };
};

export const CATALOG_LIST_DRAGGABLE_BLOCK_WIDTH = {
  threeColumns: { touch: 305, mouse: 318 },
  stacked: 205,
};

export const CATALOG_LIST_RIGHT_BLOCK_GAP = 8;

const CATALOG_LIST_ADD_BUTTON_MIN_WIDTH = 60;

export const CATALOG_LIST_RIGHT_BLOCK_WIDTH =
  COMBINATION_PRICE_SECTION_WIDTH +
  CATALOG_LIST_RIGHT_BLOCK_GAP +
  CATALOG_LIST_ADD_BUTTON_MIN_WIDTH;

export const CATALOG_LIST_CHECKBOX_SLOT_WIDTH = 24;

export const CATALOG_LIST_CHECKBOX_GAP: CatalogListTouchAxis = { touch: 8, mouse: 4 };

interface GetCatalogListLeftColumnWidthParams {
  isTouchEnabled: boolean;
  hasSelection: boolean;
}

export const getCatalogListLeftColumnWidth = ({
  isTouchEnabled,
  hasSelection,
}: GetCatalogListLeftColumnWidthParams): number => {
  const touchKey = isTouchEnabled ? 'touch' : 'mouse';
  const checkboxSlotWidth = hasSelection
    ? CATALOG_LIST_CHECKBOX_SLOT_WIDTH + CATALOG_LIST_CHECKBOX_GAP[touchKey]
    : 0;

  return CATALOG_LIST_DRAGGABLE_BLOCK_WIDTH.threeColumns[touchKey] + checkboxSlotWidth;
};

export const getCatalogListRowHeight = ({
  isStacked,
  isTouchEnabled,
}: CatalogListRowAxesParams): number => {
  const centerBlockHeight = getCatalogListCenterBlockHeight({ isStacked, isTouchEnabled });

  const topBlockHeight = getCatalogListTopBlockHeight({ isStacked, isTouchEnabled });

  const contentHeight = isStacked
    ? topBlockHeight + CATALOG_LIST_STACKED_INFO_TO_CHIPS_GAP + centerBlockHeight
    : Math.max(topBlockHeight, centerBlockHeight);

  return 2 * CATALOG_LIST_ROW_VERTICAL_PADDING + contentHeight;
};
